import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { PendingRoute, PendingSession } from "./types";

interface OfflineSchema extends DBSchema {
  pendingSessions: { key: number; value: PendingSession };
  pendingRoutes: { key: number; value: PendingRoute; indexes: { sessionId: number } };
  meta: { key: string; value: number };
}

let dbPromise: Promise<IDBPDatabase<OfflineSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<OfflineSchema>> {
  dbPromise ??= openDB<OfflineSchema>("sendstats-offline", 1, {
    upgrade(db) {
      db.createObjectStore("pendingSessions", { keyPath: "id" });
      const routes = db.createObjectStore("pendingRoutes", { keyPath: "id" });
      routes.createIndex("sessionId", "sessionId");
      db.createObjectStore("meta");
    },
  });
  return dbPromise;
}

// Only for tests, so each test starts against a fresh in-memory fake-indexeddb.
// Closes the current connection first — otherwise a subsequent
// indexedDB.deleteDatabase() in the test setup blocks forever waiting for a
// connection that's still open.
export async function _resetDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
  }
  dbPromise = null;
}

// A persisted, monotonically-decreasing counter (not Date.now()) so ids stay
// unique across app restarts even with still-pending records already on disk.
export async function nextLocalId(): Promise<number> {
  const db = await getDb();
  const tx = db.transaction("meta", "readwrite");
  const current = (await tx.store.get("counter")) ?? 0;
  const next = current - 1;
  await tx.store.put(next, "counter");
  await tx.done;
  return next;
}

export function isLocalId(id: number): boolean {
  return id < 0;
}

export async function putPendingSession(session: PendingSession): Promise<void> {
  await (await getDb()).put("pendingSessions", session);
}

export async function getPendingSession(id: number): Promise<PendingSession | undefined> {
  return (await getDb()).get("pendingSessions", id);
}

export async function deletePendingSession(id: number): Promise<void> {
  await (await getDb()).delete("pendingSessions", id);
}

export async function listPendingSessions(): Promise<PendingSession[]> {
  return (await getDb()).getAll("pendingSessions");
}

export async function putPendingRoute(route: PendingRoute): Promise<void> {
  await (await getDb()).put("pendingRoutes", route);
}

export async function getPendingRoute(id: number): Promise<PendingRoute | undefined> {
  return (await getDb()).get("pendingRoutes", id);
}

export async function deletePendingRoute(id: number): Promise<void> {
  await (await getDb()).delete("pendingRoutes", id);
}

export async function listPendingRoutes(): Promise<PendingRoute[]> {
  return (await getDb()).getAll("pendingRoutes");
}

export async function listPendingRoutesForSession(sessionId: number): Promise<PendingRoute[]> {
  return (await getDb()).getAllFromIndex("pendingRoutes", "sessionId", sessionId);
}

/**
 * Delete everything one account has queued on this device. Called on an
 * explicit logout, so a shared device keeps none of that user's unsynced
 * climbs or photos. Other accounts' queues (e.g. one whose session expired
 * before it synced) are left for that user's next sign-in. The local id
 * counter is kept, so a new record can never reuse a cleared record's id.
 */
export async function clearOfflineDataForUser(userId: number): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["pendingSessions", "pendingRoutes"], "readwrite");
  const deletedRouteIds: number[] = [];
  for (const storeName of ["pendingSessions", "pendingRoutes"] as const) {
    let cursor = await tx.objectStore(storeName).openCursor();
    while (cursor) {
      if (cursor.value.createdByUserId === userId) {
        if (storeName === "pendingRoutes") deletedRouteIds.push(cursor.value.id);
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }
  }
  await tx.done;
  deletedRouteIds.forEach(revokeObjectUrl);
}

// Object URLs for locally-stored photo Blobs, cached per pending-route id so
// repeated reads (e.g. SessionDetailPage's video-transcode poll) don't leak
// a fresh URL on every render.
const objectUrlCache = new Map<number, string>();

export function blobToObjectUrl(id: number, blob: Blob): string {
  let url = objectUrlCache.get(id);
  if (!url) {
    url = URL.createObjectURL(blob);
    objectUrlCache.set(id, url);
  }
  return url;
}

export function revokeObjectUrl(id: number): void {
  const url = objectUrlCache.get(id);
  if (url) {
    URL.revokeObjectURL(url);
    objectUrlCache.delete(id);
  }
}
